import React, { useEffect, useMemo, useState } from "react";
import type { ClimateThresholds } from "../App";

interface SettingsModalProps {
  onClose: () => void;
  thresholds: ClimateThresholds;
  defaultThresholds: ClimateThresholds;
  onApply: (thresholds: ClimateThresholds) => void;
  onReset: () => void;
  darkMode: boolean;
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
}

type EditorMode = "global" | "custom";

const SettingsModal: React.FC<SettingsModalProps> = ({
  onClose,
  thresholds,
  defaultThresholds,
  onApply,
  onReset,
  darkMode,
  setDarkMode,
}) => {
  const zoneNames = useMemo(
    () =>
      Array.from(
        new Set([
          ...Object.keys(defaultThresholds),
          ...Object.keys(thresholds),
        ])
      ).sort(),
    [defaultThresholds, thresholds]
  );
  const [mode] = useState<EditorMode>("global");
  const [draftThresholds, setDraftThresholds] = useState<ClimateThresholds>({});
  const [globalThresholds, setGlobalThresholds] = useState({
    green: 0,
    orange: 0,
  });

  useEffect(() => {
    setDraftThresholds(thresholds);
    const firstZone = zoneNames[0];
    const baseThreshold = firstZone
      ? thresholds[firstZone] ?? defaultThresholds[firstZone]
      : { green: 0, orange: 0 };

    setGlobalThresholds({
      green: baseThreshold?.green ?? 0,
      orange: baseThreshold?.orange ?? 0,
    });
  }, [defaultThresholds, thresholds, zoneNames]);

  const handleSave = () => {
    if (zoneNames.length === 0) {
      onClose();
      return;
    }

    if (mode === "global") {
      const nextThresholds = zoneNames.reduce<ClimateThresholds>((acc, zone) => {
        acc[zone] = {
          green: globalThresholds.green,
          orange: globalThresholds.orange,
        };
        return acc;
      }, {});
      onApply(nextThresholds);
      return;
    }

    onApply(draftThresholds);
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.55)", zIndex: 2050 }}
    >
      <div
        className="bg-white rounded shadow-lg w-100"
        style={{ maxWidth: "860px", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-start p-4 border-bottom">
          <div>
            <p className="text-uppercase text-primary fw-semibold small mb-2">
              Settings
            </p>
            <h3 className="mb-0">Preferences</h3>
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
            type="button"
          >
            Close
          </button>
        </div>

        <div className="p-4">
          <section className="mb-4">
            <h4 className="h6 fw-bold mb-3">Mode</h4>
            <div className="d-flex flex-column flex-sm-row gap-2">
              <button
                type="button"
                className={`btn ${!darkMode ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setDarkMode(false)}
              >
                Light
              </button>
              <button
                type="button"
                className={`btn ${darkMode ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setDarkMode(true)}
              >
                Dark
              </button>
            </div>
          </section>

          {/*}
          <section className="pt-4 border-top">
            <h4 className="h5 fw-bold mb-2">Climate Threshold Preferences</h4>
            <p className="text-muted mb-3">
              Update thresholds temporarily for scenario visuals. Refreshing the
              page will restore the backend defaults.
            </p>

            <div className="alert alert-secondary mb-4">
              Threshold preferences will not apply directly to the map because
              applying them spatially requires additional computation. The selected
              thresholds will be included in the download package from the Planning
              Tool so they can be used for visualization in a GIS tool.
            </div>

            <div className="d-flex flex-column flex-md-row gap-3 mb-4">
              <button
                type="button"
                className={`btn ${mode === "global" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setMode("global")}
              >
                Apply to All Climate Zones
              </button>
              <button
                type="button"
                className={`btn ${mode === "custom" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setMode("custom")}
              >
                Customize Each Climate Zone
              </button>
            </div>

            {zoneNames.length === 0 ? (
              <div className="alert alert-warning mb-0">
                Threshold data is not available yet, so there is nothing to edit.
              </div>
            ) : mode === "global" ? (
              <div className="row g-3">
                <div className="col-md-6">
                  <label htmlFor="global-green" className="form-label fw-semibold">
                    Green Threshold (mm)
                  </label>
                  <input
                    id="global-green"
                    type="number"
                    className="form-control form-control-lg"
                    value={globalThresholds.green}
                    onChange={(e) =>
                      setGlobalThresholds((current) => ({
                        ...current,
                        green: Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="global-orange" className="form-label fw-semibold">
                    Orange Threshold (mm)
                  </label>
                  <input
                    id="global-orange"
                    type="number"
                    className="form-control form-control-lg"
                    value={globalThresholds.orange}
                    onChange={(e) =>
                      setGlobalThresholds((current) => ({
                        ...current,
                        orange: Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div className="col-12">
                  <p className="mb-0" style={{ color: "#dc3545" }}>
                    Saving in this mode applies the same green and orange thresholds
                    to every climate zone.
                  </p>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Climate Zone</th>
                      <th>Green Threshold (mm)</th>
                      <th>Orange Threshold (mm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zoneNames.map((zone) => (
                      <tr key={zone}>
                        <td className="fw-semibold">{zone}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={draftThresholds[zone]?.green ?? 0}
                            onChange={(e) =>
                              handleZoneThresholdChange(zone, "green", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            value={draftThresholds[zone]?.orange ?? 0}
                            onChange={(e) =>
                              handleZoneThresholdChange(zone, "orange", e.target.value)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          */}
        </div>

        <div className="d-flex flex-column flex-md-row justify-content-between gap-3 p-4 border-top bg-light">
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={onReset}
            disabled={zoneNames.length === 0}
          >
            Reset to Default
          </button>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
            >
              Save Preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
